import {
    ComparatorPicker,
    ComparatorPickerBuilder,
    ComparatorPickerType,
    ComparisonOperator,
    Field,
    FieldType,
} from "../../vforms-model";
import { DatetimeComponent } from "./datetime.component";
import { DatetimeEditorComponent } from "./datetime-editor.component";
import { assign } from "lodash-es";
import { TextBox } from "../text-box/text-box";
import { ComparisonExpression } from "../../expression-settings/model";
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    isSameDay,
    isSameMonth,
    isSameWeek,
    isSameYear,
    setHours,
    setMinutes,
} from "date-fns";

export enum DatetimeLayout {
    Date = 0,
    Time = 1,
    Datetime = 2,
    Range = 3,
}

export class DatetimeType extends FieldType {
    static Id = "datetime";

    constructor() {
        super();
        this.id = DatetimeType.Id;
        this.icon = "mat_outline:calendar_today";
        this.previewComponent = DatetimeComponent;
        this.editorComponent = DatetimeEditorComponent;
    }

    createField(): Field {
        return new Datetime(this);
    }

    supportedComparisonOperators(): ComparisonOperator[] {
        return [
            ...super.supportedComparisonOperators(),
            ComparisonOperator.Today,
            ComparisonOperator.Yesterday,
            ComparisonOperator.CurrentWeek,
            ComparisonOperator.LastWeek,
            ComparisonOperator.CurrentMonth,
            ComparisonOperator.LastMonth,
            ComparisonOperator.CurrentYear,
            ComparisonOperator.LastYear,
        ];
    }

    evaluate(expression: ComparisonExpression, value: any): boolean {
        let now = setMinutes(setHours(new Date(), 0), 0);
        let date: number | undefined = value ? Date.parse(value) : undefined;
        switch (expression.operator) {
            case ComparisonOperator.Today:
                return isSameDay(date, now);
            case ComparisonOperator.Yesterday:
                return isSameDay(date, addDays(now, -1));
            case ComparisonOperator.CurrentWeek:
                return isSameWeek(now, date);
            case ComparisonOperator.LastWeek:
                return isSameWeek(addWeeks(now, -1), date);
            case ComparisonOperator.CurrentMonth:
                return isSameMonth(now, date);
            case ComparisonOperator.LastMonth:
                return isSameMonth(addMonths(now, -1), date);
            case ComparisonOperator.CurrentYear:
                return isSameYear(now, date);
            case ComparisonOperator.LastYear:
                return isSameYear(addYears(now, -1), date);
        }

        return super.evaluate(expression, value);
    }
}

export class Datetime extends TextBox {
    layout: DatetimeLayout;

    constructor(type: FieldType) {
        super(type);
        this.layout = DatetimeLayout.Date;
    }

    toJson() {
        const definition = super.toJson();
        assign(definition, {
            layout: this.layout,
        });
        return definition;
    }
}

export class DatetimeComparatorPickerBuilder extends ComparatorPickerBuilder {
    build(comparisonExpression: ComparisonExpression): ComparatorPicker {
        let noneOperators = [
            ComparisonOperator.NotEmpty,
            ComparisonOperator.Empty,
            ComparisonOperator.Today,
            ComparisonOperator.Yesterday,
            ComparisonOperator.CurrentWeek,
            ComparisonOperator.LastWeek,
            ComparisonOperator.CurrentMonth,
            ComparisonOperator.LastMonth,
            ComparisonOperator.CurrentYear,
            ComparisonOperator.LastYear,
        ];

        if (noneOperators.findIndex((o) => o === comparisonExpression.operator) >= 0) {
            return new ComparatorPicker(ComparatorPickerType.None);
        }

        return new ComparatorPicker(ComparatorPickerType.Date);
    }
}
